import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { STATUS_OPTIONS } from "@/components/AttendanceSheetDialog";
import { CHIP } from "@/components/MemberAttendanceCard";
import { formatDateOnly, formatDateTime } from "@/lib/date";

export const stamp = (iso) => (iso ? formatDateTime(iso, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export function StatusChip({ status, testId }) {
  if (!status) return <span className="text-xs text-slate-300" data-testid={testId}>Not recorded</span>;
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${CHIP[status]}`} data-testid={testId}>{status}</span>;
}

// Pick a status (+ optional note) and save just this one record. `onSave` resolves true on success.
export function StatusEditor({ status, note, onSave, onDone, testPrefix }) {
  const [st, setSt] = useState(status || "present");
  const [nt, setNt] = useState(note || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const ok = await onSave(st, st === "present" ? "" : nt);
    setBusy(false);
    if (ok) onDone?.();
  };
  return (
    <div className="space-y-2" data-testid={`${testPrefix}-editor`} onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap gap-1" role="radiogroup">
        {STATUS_OPTIONS.map(({ key, label, Icon, on }) => (
          <button key={key} type="button" role="radio" aria-checked={st === key} onClick={() => setSt(key)} data-testid={`${testPrefix}-opt-${key}`}
            className={`flex h-7 items-center gap-1 rounded-full px-2.5 text-xs font-semibold transition-colors ${st === key ? on : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>
      {st !== "present" && <Input value={nt} onChange={(e) => setNt(e.target.value)} placeholder="Optional note" className="h-8 text-sm" data-testid={`${testPrefix}-note`} />}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" className="h-8 rounded-full" onClick={() => onDone?.()} data-testid={`${testPrefix}-cancel`}>Cancel</Button>
        <Button size="sm" className="h-8 rounded-full bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={save} data-testid={`${testPrefix}-save`}>
          {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Save
        </Button>
      </div>
    </div>
  );
}

// Grid cell → quick status picker for one member on one session.
export function CellPicker({ row, event, onSave, testId, children }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} className="rounded-full transition-shadow hover:ring-2 hover:ring-slate-300" data-testid={`${testId}-btn`}>{children}</button>
      </PopoverTrigger>
      <PopoverContent className="w-80" onClick={(e) => e.stopPropagation()} data-testid={`${testId}-picker`}>
        <p className="text-sm font-semibold text-slate-800">{row.member_name}</p>
        <p className="mb-3 text-xs text-slate-500">{event.title} · {formatDateOnly(event.event_date)}</p>
        <StatusEditor status={row.cells?.[event.id]} note={row.notes?.[event.id]} testPrefix={testId}
          onSave={(st, nt) => onSave(event.id, row.member_id, st, nt)} onDone={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
